import { memo } from 'react'
import { MoreHorizontal, Trash2 } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import type { UserSession } from '../types/sessionList'

interface SessionItemProps {
  session: UserSession
  onNavigate: (session: UserSession) => void
  onDelete: (sessionId: string) => void
}

/**
 * 展开状态下的会话项 - 显示完整信息
 */
export const SessionItemExpanded = memo(function SessionItemExpanded({
  session,
  onNavigate,
  onDelete,
}: SessionItemProps) {
  const { sessionId, engine } = session
  const { setOpenMobile } = useSidebar()

  const handleClick = () => {
    setOpenMobile(false)
    onNavigate(session)
  }

  return (
    <SidebarMenuItem className='group/session relative'>
      <SidebarMenuButton
        className='h-auto cursor-pointer py-2 pr-8'
        onClick={handleClick}
      >
        <Avatar className='h-8 w-8 shrink-0'>
          <AvatarImage src={engine.engineCoverURL} alt={engine.engineTitle} />
          <AvatarFallback className='text-xs'>
            {engine.engineTitle.slice(0, 2)}
          </AvatarFallback>
        </Avatar>
        <span className='truncate'>{engine.engineTitle}</span>
      </SidebarMenuButton>

      {/* 移动端始终显示，PC端 hover 时显示的更多操作按钮 */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={`hover:bg-accent } absolute top-1/2 right-2 -translate-y-1/2 rounded p-1 transition-opacity`}
            onClick={(e) => e.stopPropagation()}
          >
            <MoreHorizontal className='h-4 w-4' />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side='right' align='start'>
          <DropdownMenuItem
            className='text-destructive focus:text-destructive'
            onClick={() => onDelete(sessionId)}
          >
            <Trash2 className='mr-2 h-4 w-4' />
            删除会话
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  )
})

/**
 * 折叠状态下的会话项 - 仅显示图标，hover 显示 tooltip/dropdown
 */
export const SessionItemCollapsed = memo(function SessionItemCollapsed({
  session,
  onNavigate,
  onDelete,
}: SessionItemProps) {
  const { sessionId, engine } = session
  const { setOpenMobile } = useSidebar()

  const handleNavigate = () => {
    setOpenMobile(false)
    onNavigate(session)
  }

  return (
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            tooltip={engine.engineTitle}
            className='h-auto py-2'
          >
            <Avatar className='h-6 w-6 shrink-0'>
              <AvatarImage
                src={engine.engineCoverURL}
                alt={engine.engineTitle}
              />
              <AvatarFallback className='text-xs'>
                {engine.engineCoverURL ?? engine.engineTitle.slice(0, 1)}
              </AvatarFallback>
            </Avatar>
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent side='right' align='start' sideOffset={4}>
          <DropdownMenuItem onClick={handleNavigate}>
            <span className='max-w-52 truncate'>{engine.engineTitle}</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            className='text-destructive focus:text-destructive'
            onClick={() => onDelete(sessionId)}
          >
            <Trash2 className='mr-2 h-4 w-4' />
            删除会话
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  )
})
